// The unit itself: its name, its number, and its moods.
//
// There is exactly one machine, so this wraps the generated list store into a
// singleton the way the chassis does for a profile. Everything the header line
// prints, and every switch in SETUP, lives here.
//
// THE MOODS ARE THE SECOND ACT. Every persona who said they would delete this
// app said the same thing: there is no reason for day two. A prop with state
// that misbehaves on its own is the answer — the page counter only ever goes
// up, the toner runs down and lightens the output until the cartridge is
// shaken, and eventually it jams and has to be cleared.

import { computed } from 'vue';
import { machines, addMachine, saveMachine, storeReady, faxs, removeFax } from '../store.js';

const DEFAULTS = {
  tsi: '',
  number: '',
  dateFormat: 'dmy',
  pageCount: 0,
  toner: 100,
  jammed: false,
  speaker: true,
  largeControls: false,
  plainEnglish: false,
  toldOnce: false,
};

let ensuring = null;

/** Create the single machine row on first ever launch. */
export function ensureMachine() {
  if (!ensuring) {
    ensuring = storeReady.then(() => {
      if (!machines.value.length) {
        addMachine({ ...DEFAULTS, number: randomNumber() });
      }
      return machines.value[0];
    });
  }
  return ensuring;
}

export const machine = computed(() => machines.value[0] ?? { id: 0, ...DEFAULTS });

export function updateMachine(patch) {
  const row = machines.value[0];
  if (!row) return null;
  const next = { ...row, ...patch };
  saveMachine(next);
  return next;
}

/** A fictional number, in the shape of a real one. Never dialled by anything. */
function randomNumber() {
  const p = () => Math.floor(Math.random() * 9000 + 1000);
  return `+61 2 ${p()} ${p()}`;
}

/** The name printed on every outgoing fax. Unnamed is a wasted joke. */
export const tsi = computed(() => machine.value.tsi || 'UNNAMED MACHINE');

export const needsName = computed(() => !machine.value.tsi);

/** Toner falls with use. Below this the page is visibly pale. */
export const TONER_LOW = 25;
export const tonerLow = computed(() => machine.value.toner <= TONER_LOW);

/**
 * One page has gone through. Called once per completed transmission.
 *
 * The jam is deliberately not random on every page — it becomes possible only
 * once the machine has done enough work to have earned it, so a first-run user
 * never meets one and a regular does.
 */
export function recordPage() {
  const m = machine.value;
  const pageCount = (m.pageCount || 0) + 1;
  const toner = Math.max(0, (m.toner ?? 100) - 2.5);
  const jammed = pageCount > 10 && !m.jammed && Math.random() < 0.06;
  return updateMachine({ pageCount, toner, jammed });
}

/** Shaking the cartridge is how you got another dozen pages out of it. */
export function shakeCartridge() {
  return updateMachine({ toner: Math.min(100, (machine.value.toner ?? 0) + 35) });
}

export function clearJam() {
  return updateMachine({ jammed: false });
}

/**
 * HOW MUCH PAPER THE OUT TRAY HOLDS.
 *
 * The design says fifty, and nothing enforced it — so the outbox grew without
 * limit, and every row carries a 1080px source image plus a thumbnail. Fifty
 * faxes is already ten to twenty megabytes of IndexedDB; five hundred is a
 * quota error on a write, which surfaces as a fax that renders perfectly,
 * announces itself complete, and is not there tomorrow. A cap you can see
 * working is better than a limit you meet by accident.
 *
 * The tray sheds the OLDEST page, never the newest, and it says so — in the
 * machine's voice, because an out tray overflowing is a thing a machine does.
 */
export const TRAY_CAPACITY = 50;

export function pruneTray() {
  const rows = [...faxs.value].sort(
    (a, b) => String(a.createdAt || '').localeCompare(String(b.createdAt || ''))
        || Number(a.id) - Number(b.id),
  );
  const over = rows.length - TRAY_CAPACITY;
  if (over <= 0) return 0;
  for (const row of rows.slice(0, over)) removeFax(row.id);
  return over;
}

/**
 * A date the way this machine prints it.
 *
 * The outbox listed a recipient, a subject and a state, and no date at all —
 * so two faxes to the same person were indistinguishable, and "the one I sent
 * last night" was not a question the screen could answer. DATE FORMAT is
 * already a setting; this is the only other place it can possibly mean
 * anything, since the header line is burned into the image.
 */
export function stamp(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const two = (n) => String(n).padStart(2, '0');
  const day = two(d.getDate());
  const month = two(d.getMonth() + 1);
  const date = machine.value.dateFormat === 'mdy' ? `${month}/${day}` : `${day}/${month}`;
  return `${date} ${two(d.getHours())}:${two(d.getMinutes())}`;
}
